import { Typography, Paper, Chip, Grid, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CheckDetailTitle from './CheckDetailTitle'
import TimelineDotLine from '../components/TimelineDotLine'
import CheckRiskStatus from '../components/CheckRiskStatus'
import AmlddSearchTerms from './AmlddSearchTerms'
import {
  renderPepFields,
  renderEsgFields,
  renderLriFields,
  renderMtlsnSimpleFields,
  renderMtlsnFields,
  renderMtlsnNewsFields
} from './amlddConfigs'

/**
 * Unified component for all AMLDD check types
 * Handles PEP, ESG, LRI, MTLSN (Simple, Full, News)
 */
const CheckAmldd = ({
  type,
  // Accept all possible prop names
  check,
  amlddPepCheck,
  amlddEsgCheck,
  amlddLriCheck,
  amlddMtlsnCheck,
  amlddMtlsnSimpleCheck,
  amlddMtlsnWithNewsCheck
}) => {
  const { t } = useTranslation(['common', 'messages'])

  // Get the actual check data from whichever prop was passed
  const checkData = check || amlddPepCheck || amlddEsgCheck || amlddLriCheck || amlddMtlsnCheck || amlddMtlsnSimpleCheck || amlddMtlsnWithNewsCheck

  // Get title key and render function based on type
  const getConfig = () => {
    switch (type) {
      case 'PEP':
        return {
          titleKey: 'common:amlddPep',
          renderFields: renderPepFields
        }
      case 'ESG':
        return {
          titleKey: 'common:amlddEsg',
          renderFields: renderEsgFields
        }
      case 'LRI':
        return {
          titleKey: 'common:amlddLri',
          renderFields: renderLriFields
        }
      case 'MTLSN_SIMPLE':
        return {
          titleKey: 'common:amlddMtlsnSimple',
          renderFields: renderMtlsnSimpleFields
        }
      case 'MTLSN':
        return {
          titleKey: 'common:amlddMtlsn',
          renderFields: renderMtlsnFields
        }
      case 'MTLSN_NEWS':
        return {
          titleKey: 'common:amlddMtlsnWithNews',
          renderFields: renderMtlsnNewsFields
        }
      default:
        return {
          titleKey: 'common:amldd',
          renderFields: null
        }
    }
  }

  const { titleKey, renderFields } = getConfig()

  const checkDetails = checkData?.checkDetails
  const quantidade = checkDetails?.quantidade || 0
  const cadastros = checkDetails?.cadastros || []
  const busca = checkDetails?.busca || []

  const hasChecks = quantidade > 0
  const checkLength = quantidade

  return (
    <>
      <Paper elevation={0} variant="colorPrimary" sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography>{t(titleKey)}</Typography>
        <CheckRiskStatus checkLength={checkLength} hasChecks={hasChecks} />
      </Paper>

      <CheckDetailTitle
        email={checkData?.email}
        status={checkData?.status}
        createdAt={checkData?.createdAt}
        updatedAt={checkData?.updatedAt}
      />

      {hasChecks && (
        <TimelineDotLine>
          <Paper variant="rootOutlined" elevation={0} sx={{ flex: 1, py: '10px' }}>
            <div className="c-detail-grid">
              {/* Summary */}
              <div className="c-detail-row">
                <Typography variant="subtitle1">{t('common:amldd.totalRecords')}</Typography>
                <Typography variant="subtitle3">
                  <Chip
                    label={quantidade > 0 ? quantidade : t('common:amldd.noRecordsFound')}
                    color={quantidade > 0 ? 'error' : 'success'}
                    size="small"
                    sx={{ color: 'white' }}
                  />
                </Typography>
              </div>

              {/* Search Terms */}
              <AmlddSearchTerms busca={busca} />

              {/* No Records Message */}
              {quantidade === 0 && (
                <div style={{ gridColumn: '1 / 3' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: 'rgba(76, 175, 80, 0.08)',
                      border: '1px solid rgba(76, 175, 80, 0.2)'
                    }}
                  >
                    <Typography color="success.main" fontWeight="bold" variant="body1">
                      {t('common:amldd.noRecordsFound')}
                    </Typography>
                  </Paper>
                </div>
              )}

              {/* Records Found */}
              {cadastros.length > 0 && (
                <div style={{ gridColumn: '1 / 3' }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {t('common:amldd.recordsFound')} ({cadastros.length})
                  </Typography>
                  {cadastros.map((record, index) => (
                    <Paper
                      key={record.id || index}
                      elevation={0}
                      sx={{ mb: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {record.nome || `${t('common:amldd.record')} ${index + 1}`}
                          </Typography>
                        </Grid>

                        {/* Render type-specific fields */}
                        {renderFields && renderFields(record, t)}
                      </Grid>
                    </Paper>
                  ))}
                </div>
              )}
            </div>
          </Paper>
        </TimelineDotLine>
      )}
    </>
  )
}

export default CheckAmldd
